import { deepFreeze, isSubscribableResult, isUserVisibleError } from '@conduit-client/utils';
import {
    type GraphQLCommandError,
    type GraphQLResponse,
    type GraphQLCommand,
} from '@conduit-client/graphql-normalization/v1';
import {
    type ResolveAndValidateOptions,
    resolveAndValidateGraphQLConfig,
    toGraphQLErrorResponse,
} from '@conduit-client/onestore-graphql-parser/v1';
import {
    type JsonSchemaViolationError,
    type JSONSchema,
} from '@conduit-client/jsonschema-validate';
import {
    type WireAdapterConstructor,
    type WireConfigValue,
    type WireContextValue,
    type GraphQLWireAdapterResult,
} from '@conduit-client/lwc-types';
import {
    CommandWireAdapterConstructor,
    type GetCommand,
    toGraphQLResponseFromFailure,
} from './utils';
import sanitize from './sanitize';
import type {
    Result,
    ServiceDescriptor,
    SubscribableResult,
    NamedService,
} from '@conduit-client/utils';

export abstract class GraphQLCommandWireAdapterConstructor<
    Config extends WireConfigValue = WireConfigValue,
> extends CommandWireAdapterConstructor<GraphQLResponse, Config, GraphQLCommand> {
    protected emit(
        result?:
            | SubscribableResult<GraphQLResponse, GraphQLCommandError>
            | Result<GraphQLResponse, GraphQLCommandError>
            | undefined
    ): void {
        try {
            if (result === undefined) {
                this.callback({ data: undefined, errors: undefined });
            } else {
                const consumerEmittedRefresh = (): Promise<void> => {
                    if (!this.refresh) {
                        return Promise.resolve();
                    }
                    return new Promise((resolve, reject) => {
                        if (!this.refresh) {
                            resolve();
                            return;
                        }
                        this.refresh().then((res) => {
                            if (res.isOk()) {
                                resolve();
                                return;
                            }

                            if (isUserVisibleError(res.error)) {
                                resolve();
                                return;
                            }

                            reject(
                                new Error(
                                    'Internal error in GraphQL adapter occurred: Failed to refresh GraphQL data'
                                )
                            );
                        });
                    });
                };

                let consumerEmittedData = {
                    data: undefined,
                    errors: undefined,
                } as {
                    data: GraphQLResponse['data'];
                    errors: GraphQLResponse['errors'];
                    refresh?: () => Promise<void>;
                };

                if (this.exposeRefresh && this.refresh) {
                    consumerEmittedData.refresh = consumerEmittedRefresh;
                }

                if (result.isErr()) {
                    const failure = isSubscribableResult(result)
                        ? result.error.failure
                        : result.error;
                    const resp = toGraphQLResponseFromFailure(failure);
                    consumerEmittedData.data = resp.data;
                    consumerEmittedData.errors = resp.errors;
                } else {
                    consumerEmittedData.data = result.value.data;
                }

                deepFreeze(consumerEmittedData);
                this.callback(consumerEmittedData);
            }
        } catch (e) {
            this.handleExecutionThrow(e);
        }
    }

    protected handleExecutionThrow(error: unknown): void {
        this.callback(toGraphQLErrorResponse(error));
    }

    protected handleConfigSchemaViolation(error: JsonSchemaViolationError): void {
        this.handleExecutionThrow(error);
    }

    update(config: Config, _context?: WireContextValue): void {
        this.unsubscribe();

        // Centralized parsing error handling (handles null/undefined query)
        const options: ResolveAndValidateOptions = {
            acceptedOperations: ['query'],
        };
        const result = resolveAndValidateGraphQLConfig(config, options);

        if (!result) {
            return;
        }

        if (result.isErr()) {
            const error = result.error;
            // Emit error response for actual parsing errors
            this.callback({
                data: undefined,
                errors: error.errors,
            } satisfies GraphQLResponse);
            return;
        }

        // Sanitize everything except query (DocumentNode must not be sanitized for AST resolution)
        const { query, ...rest } = result.value;
        this.config = {
            query,
            ...sanitize(rest),
        } as unknown as Config;
        this.invokeAdapter();
    }
}

export class LWCGraphQLWireBindingsService {
    bind<Config extends WireConfigValue = WireConfigValue>(
        getCommand: GetCommand<GraphQLResponse, Config, GraphQLCommand>,
        configSchema?: JSONSchema,
        exposeRefresh = false
    ): WireAdapterConstructor<Config, GraphQLWireAdapterResult, {}> {
        return class extends GraphQLCommandWireAdapterConstructor<Config> {
            configSchema = configSchema;
            exposeRefresh = exposeRefresh;
            getCommand() {
                return getCommand(this.config!);
            }
        };
    }
}

export type LWCGraphQLWireBindingsServiceDescriptor = ServiceDescriptor<
    LWCGraphQLWireBindingsService,
    'lwcGraphQLWireBindings',
    '1.0'
>;

export type NamedLWCGraphQLWireBindingsService<Name extends string = 'lwcGraphQLWireBindings'> =
    NamedService<Name, LWCGraphQLWireBindingsService>;

export function buildServiceDescriptor(): LWCGraphQLWireBindingsServiceDescriptor {
    return {
        type: 'lwcGraphQLWireBindings',
        version: '1.0',
        service: new LWCGraphQLWireBindingsService(),
    };
}
